/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { ScatterSeriesOption } from "echarts";
import { CallbackDataParams, TopLevelFormatterParams } from "echarts/types/dist/shared";
import { CalculatorResponse } from "../../models/http/calculator/calculator-response.model";
import { BarChartSerie } from "../../models/http/charts/bar-chart/bar-chart-serie.model";
import { BarChart } from "../../models/http/charts/bar-chart/bar-chart.model";
import { Bar } from "../../models/http/charts/bar-chart/bar.model";
import { LineChart } from "../../models/http/charts/line-chart/line-chart.model";
import { LinePoint } from "../../models/http/charts/line-chart/line-point.model";
import { MapChart } from "../../models/http/charts/map-chart/map-chart.model";
import { MapRegion } from "../../models/http/charts/map-chart/map-region.model";
import { MixedChart } from "../../models/http/charts/mixed-chart/mixed-chart.model";
import { MixedValue } from "../../models/http/charts/mixed-chart/mixed-value.model";
import { PieChartSlice } from "../../models/http/charts/pie-chart/pie-chart-slice.model";
import { PieChart } from "../../models/http/charts/pie-chart/pie-chart.model";
import { ScatterChart } from "../../models/http/charts/scatter-chart/scatter-chart.model";
import { ScatterPoint } from "../../models/http/charts/scatter-chart/scatter-point.model";
import { ChartResponse, ChartType, SerieDataWithThreshold } from "../../models/ui/charts.model";
import { COLORS, MAP_CHART_MAX_RANGE, MAP_CHART_MIN_RANGE } from "../constants";
import { formatNumberMaxDecimals, isNullOrUndefined, roundValueUpTo } from "../utils";

/**
 * Builds the mark line options for a scatter series in ECharts.
 *
 * @param data - The data containing threshold values for the X and Y axes.
 * @returns The mark line options for the scatter series.
 */
export function buildScatterMarkLineOptions(
	data: SerieDataWithThreshold
): ScatterSeriesOption["markLine"] {
	return {
		symbol: "none",
		tooltip: {
			show: false
		},
		label: {
			borderWidth: 0,
			color: COLORS.WHITE
		},
		data: [
			{
				xAxis: data.thresholdX,
				lineStyle: {
					type: "solid",
					color: COLORS.WHITE
				},
				label: {
					align: "left",
					verticalAlign: "top",
					padding: [8, 0, 0, 8]
				}
			},
			{
				yAxis: data.thresholdY,
				lineStyle: {
					type: "solid",
					color: COLORS.WHITE
				},
				label: {
					verticalAlign: "top",
					padding: [8, 0, 0, -20]
				}
			}
		]
	};
}

/**
 * Returns the color for a bar in a bar chart series.
 *
 * @param series - An array of bar chart series.
 * @param serieIndex - The index of the series.
 * @param barIndex - The index of the bar within the series.
 * @param isGradient - Optional flag indicating if gradient colors should be used.
 * @returns The color for the specified bar.
 */
export function getBarColor(
	series: BarChartSerie[],
	serieIndex: number,
	barIndex: number,
	isGradient?: boolean,
	isGradientInverted?: boolean
) {
	if (series.length === 1 && !isGradient) return COLORS.YELLOW_100;

	if (series.length === 1 && isGradient) {
		const possibleColors = isGradientInverted
			? [COLORS.GREEN_50, COLORS.YELLOW_50, COLORS.YELLOW_100]
			: [COLORS.YELLOW_100, COLORS.YELLOW_50, COLORS.GREEN_50];
		const partSize = Math.ceil(series[0].bars.length / possibleColors.length);
		const partIndex = Math.floor(barIndex / partSize);

		return possibleColors[partIndex];
	}

	return [
		COLORS.YELLOW_100,
		COLORS.TURQUOISE_100,
		COLORS.GREEN_50,
		COLORS.ORANGE_50,
		COLORS.TURQUOISE_50,
		COLORS.GREY_50
	][serieIndex % series.length];
}

/**
 * Calculates the total value of all slices in a pie chart series.
 *
 * @param series - An array of pie chart slices.
 * @returns The total value of all slices.
 */
export function getPieChartTotalValue(series: PieChartSlice[]) {
	return series.reduce((acc, curr) => acc + roundValueUpTo(curr.value), 0);
}

/**
 * Determines the label position for a pie chart based on the value's percentage of the total value.
 *
 * @param value - The value of the pie chart segment.
 * @param totalValue - The total value of all pie chart segments.
 * @returns The label position, either "outside" if the value is less than 20% of the total, or "inside" otherwise.
 */
export function getPieChartLabelPosition(value: number, totalValue: number) {
	return (value / totalValue) * 100 < 10 ? "outside" : "inside";
}

/**
 * Returns the appropriate color for pie chart labels based on their position.
 *
 * @param labelPosition - The position of the label. Can be "inside" or any other value.
 * @returns The color for the label. If the position is "inside", it returns `COLORS.SMART_NAVY_100`, otherwise it returns `COLORS.WHITE`.
 */
export function getPieChartLabelColor(
	labelPosition: string,
	sliceColor: string
): { labelColor: string; labelTextBorderColor: string } {
	return labelPosition === "inside"
		? {
				labelColor: COLORS.SMART_NAVY_100,
				labelTextBorderColor: isTextBorderVisible(sliceColor) ? COLORS.WHITE : "none"
			}
		: { labelColor: COLORS.WHITE, labelTextBorderColor: "none" };
}

/**
 * Determines if the text border should be visible based on the provided background color.
 *
 * @param sliceColor - The background color to check.
 * @returns `true` if the text border should be visible, `false` otherwise.
 */
export function isTextBorderVisible(sliceColor: string) {
	switch (sliceColor) {
		case COLORS.ORANGE_50:
			return true;
		default:
			return false;
	}
}

/**
 * Generates a scatter point with a specific color based on its value and thresholds.
 *
 * @param serieDataWithThreshold - An object containing threshold values for X and Y axes.
 * @param point - An object containing the scatter point value.
 * @returns An object containing the scatter point value and its item style with a specific color.
 */
export function getMappedScatterPoint(thresholdX: number, thresholdY: number, point: ScatterPoint) {
	const [xValue, yValue] = point.value;

	const color: string = getScatterPointColor(xValue, yValue, thresholdX, thresholdY);

	return {
		name: point.name,
		value: point.value,
		itemStyle: { color }
	};
}

/**
 * Determines the color of a scatter point based on its x and y values relative to specified thresholds.
 *
 * @param xValue - The x-coordinate value of the scatter point.
 * @param yValue - The y-coordinate value of the scatter point.
 * @param trasholdX - The threshold value for the x-coordinate.
 * @param trasholdY - The threshold value for the y-coordinate.
 * @returns The color of the scatter point as a hexadecimal string.
 */
export function getScatterPointColor(
	xValue: number,
	yValue: number,
	trasholdX: number,
	trasholdY: number
) {
	if (xValue >= trasholdX && yValue >= trasholdY) {
		return COLORS.ORANGE_50;
	} else if (xValue >= trasholdX && yValue < trasholdY) {
		return COLORS.ORANGE;
	} else if (xValue < trasholdX && yValue >= trasholdY) {
		return COLORS.YELLOW;
	} else {
		return COLORS.GREEN_50;
	}
}

/**
 * Returns the color associated with a given value based on specified ranges.
 *
 * @param value - The value to be checked against the ranges.
 * @param pieces - An array of objects, each containing a `min` value, a `max` value, and a `color` string.
 * @returns The color string corresponding to the range in which the value falls. If the value does not fall within any range, returns `COLORS.GREY_100`.
 */
export function getMapSelectedItemAreaColor(
	value: number,
	pieces: { min: number; max: number; color: string }[]
) {
	const lastRangeColor = pieces.pop()?.color || COLORS.ORANGE_150;

	if (isNullOrUndefined(value) || isNaN(value) || value < MAP_CHART_MIN_RANGE) {
		return COLORS.GREY_100;
	} else {
		for (const piece of pieces) {
			if (value >= piece.min && value <= piece.max) {
				return piece.color;
			}
		}

		return lastRangeColor;
	}
}

/**
 * Generates an array of range objects for mapping area colors on a chart.
 *
 * Each range object contains a `min`, `max`, and `color` property, representing
 * a value interval and its associated color. The value range from 0 to 1000 is
 * divided into 6 equal steps, each mapped to a specific color from the `COLORS` constant.
 *
 * @returns An array of objects, each specifying a value range and its corresponding color.
 */
export function getRangesColorForMapItemAreaColor() {
	const min = MAP_CHART_MIN_RANGE;
	const max = MAP_CHART_MAX_RANGE;
	const range = max - min;
	const step = range / 5;

	return [
		{
			min: min,
			max: min + step - 1,
			color: COLORS.GREEN_150
		},
		{
			min: min + step,
			max: min + 2 * step - 1,
			color: COLORS.GREEN_50
		},
		{
			min: min + 2 * step,
			max: min + 3 * step - 1,
			color: COLORS.YELLOW_100
		},
		{
			min: min + 3 * step,
			max: min + 4 * step - 1,
			color: COLORS.YELLOW_50
		},
		{
			min: min + 4 * step,
			max: min + 5 * step - 1,
			color: COLORS.ORANGE_50
		},
		{
			min: min + 5 * step,
			max: Infinity,
			color: COLORS.ORANGE_150
		}
	];
}

/**
 * Generates the HTML content for a chart tooltip.
 *
 * @param params - The parameters for the tooltip formatter.
 * @returns The HTML string for the tooltip.
 *
 * The function extracts the color, label, value, time, and unit from the provided parameters.
 * It then constructs an HTML string for the tooltip, including a colored circle, label, time (if available),
 * and value(s). If the value is a single number, it is displayed directly. If the value is an array,
 * it displays CO2 and SCI values. If no value is present, it displays "No data available".
 */
export function getChartTooltip(params: TopLevelFormatterParams, useGrouping: boolean = false) {
	const { color, borderColor, seriesType } = params as CallbackDataParams;
	const { label, value, time, unit, actualValue, actualUnit } = getTooltipParams(
		params as CallbackDataParams
	);
	const displayColor =
		borderColor && seriesType === "line" ? borderColor : color || COLORS.GREY_100;
	const isSingleValue = !Array.isArray(value);
	const isValuePresent =
		!isNullOrUndefined(value) &&
		(Array.isArray(value) ? value.length > 0 : !isNullOrUndefined(value));
	const unitMarkup = unit
		? unit === "%"
			? "%"
			: `<span class="echarts-tooltip-measure-unit">${unit}</span>`
		: "";
	const actualValueSuffix =
		typeof actualValue === "number"
			? ` - ${formatNumberMaxDecimals(actualValue, 3, true)}${
					actualUnit ? ` ${actualUnit}` : ""
				}`
			: "";
	const formattedSingleValue =
		!Array.isArray(value) && typeof value === "number"
			? formatNumberMaxDecimals(value, 3, useGrouping)
			: value;

	return `
		<div class="echarts-tooltip-container">
			<div class="echarts-tooltip-circle-container">
			    <div class="echarts-tooltip-circle-color" style="background-color: ${displayColor}"></div>
			</div>

			<span class="echarts-tooltip-label">${label}</span>

			${time ? `<span class="echarts-tooltip-time">${time}</span>` : ""}

			${
				isValuePresent
					? isSingleValue
						? `<span class="${isValuePresent ? "echarts-tooltip-value" : "echarts-tooltip-value no-data"}">
							${formattedSingleValue}${unitMarkup}${actualValueSuffix}
					       </span>`
						: `<div class="echarts-tooltip-scatter-value-container">
						       <span>CO2:</span>
						       <span class="echarts-tooltip-value">${formatNumberMaxDecimals(value[0])} KgCO2e</span>
						   </div>
						   <div class="echarts-tooltip-scatter-value-container">
						       <span>SCI:</span>
						       <span class="echarts-tooltip-value">${formatNumberMaxDecimals(value[1])}</span>
						   </div>`
					: "No data available"
			}
		</div>
	`;
}

/**
 * Extracts and returns tooltip parameters based on the chart type.
 *
 * @param params - The callback data parameters from the chart.
 * @param chartType - The type of the chart (e.g., "line", "scatter").
 * @returns An object containing the tooltip parameters:
 * - `label`: The name of the data point.
 * - `value`: The value of the data point.
 * - `time`: The time associated with the data point (only for line charts).
 * - `unit`: The unit of the data point (only for line charts).
 */
function getTooltipParams(params: CallbackDataParams) {
	const paramsData = params.data as
		| LinePoint
		| Bar
		| MapRegion
		| PieChartSlice
		| ScatterPoint
		| MixedValue;
	const actualValue = (paramsData as { actualValue?: number }).actualValue;
	const actualUnit = (paramsData as { actualUnit?: string }).actualUnit;

	if (!paramsData) {
		return {
			label: "",
			value: 0
		};
	}

	return {
		label:
			"serieName" in paramsData
				? paramsData.serieName
				: "regionName" in paramsData
					? paramsData.regionName || paramsData.name
					: paramsData.name,
		value: paramsData.value,
		...("time" in paramsData && { time: paramsData.time }),
		...("unit" in paramsData && { unit: paramsData.unit }),
		...(!isNullOrUndefined(actualValue) && { actualValue }),
		...(!isNullOrUndefined(actualUnit) && { actualUnit })
	};
}

/**
 * Generates a bar chart representing the emissions of various resources.
 *
 * @private
 * @param {CalculatorResponse} response - The response object containing emission data for different resources.
 * @returns A bar chart object with the title "Resources Emissions" and bar series for CO2 emissions of Memory, CPU, Storage, and GPU.
 */
export function getEmissionChart(response: CalculatorResponse): ChartResponse<BarChart> {
	const chart: BarChart = {
		title: "Resources Emissions",
		barSeries: [
			{
				name: "CO2",
				bars: [
					{
						name: "Memory",
						value: response.memoryEmission,
						unit: "kgCO2"
					},
					{
						name: "CPU",
						value: response.cpuEmission,
						unit: "kgCO2"
					},
					{
						name: "Storage",
						value: response.storageEmission,
						unit: "kgCO2"
					},
					{
						name: "GPU",
						value: response.gpuEmission,
						unit: "kgCO2"
					}
				]
			}
		]
	};

	return { chart, hasError: false };
}

/**
 * Generates a BarChart object based on the provided CalculatorResponse.
 *
 * @private
 * @param {CalculatorResponse} response - The response object containing simulation data.
 * @returns The BarChart object with the simulation results.
 */
export function getSimulationChart(response: CalculatorResponse): ChartResponse<BarChart> {
	const chart: BarChart = {
		title: "Results in different Locations",
		barSeries: [
			{
				name: "countries",
				bars: response.calculatorSimulations.map(simulation => {
					return {
						name: simulation.countryName,
						value: simulation.co2Emission,
						unit: simulation.emissionUnit
					};
				})
			}
		]
	};

	return { chart, hasError: false };
}

/**
 * Checks if the chart data is present based on the chart type.
 *
 * @param data - The data to be checked.
 * @param chartType - The type of the chart.
 * @returns `true` if the chart data is present, `false` otherwise.
 */
export function isChartDataPresent(data: unknown, chartType: ChartType) {
	switch (chartType) {
		case "barY":
		case "barX":
			return (
				!!data &&
				!!(data as BarChart)?.barSeries?.length &&
				!!(data as BarChart)?.barSeries.some(serie => serie.bars.length)
			);
		case "line":
			return (
				!!data &&
				!!(data as LineChart)?.lineSeries?.length &&
				!!(data as LineChart)?.lineSeries.some(serie => serie.linePoints.length)
			);
		case "map":
			return !!data && !!(data as MapChart)?.mapRegions?.length;
		case "pie":
			return !!data && !!(data as PieChart)?.PieChartSlice?.length;
		case "scatter":
			return !!data && !!(data as ScatterChart)?.scatterPoints?.length;
		case "mixed":
			return (
				!!data &&
				!!(data as MixedChart)?.series?.length &&
				!!(data as MixedChart)?.series.some(serie => serie.values.length)
			);
		default:
			return false;
	}
}

/**
 * Truncates a given label to a specified maximum length and appends an ellipsis if the label exceeds that length.
 *
 * @param label - The label string to be truncated.
 * @param maxLength - The maximum allowed length of the label before truncation.
 * @returns The truncated label with an ellipsis appended if it exceeds the maximum length, otherwise the original label.
 */
export function truncateLabelWithEllipsis(label: string | number, maxLength: number) {
	const labelAsString = label.toString();

	return labelAsString.length > maxLength
		? `${labelAsString.slice(0, maxLength)}...`
		: labelAsString;
}

/**
 * Generates a configuration object for an ECharts pie chart that displays
 * the total emissions as text in the center of the chart.
 *
 * @param total - The total emission value to be displayed in the chart.
 * @returns An object containing the `graphic` property, which defines
 *          the text elements to be displayed in the center of the pie chart.
 */
export function getHardwareAssetsTotalEmissionOverridesForPieChart(total: number) {
	return {
		series: {
			radius: ["40%", "90%"]
		},
		graphic: [
			{
				type: "text",
				left: "center",
				top: "45%",
				style: {
					text: "Total emission",
					fill: "#FFFFFF",
					fontSize: 14,
					fontWeight: "normal"
				}
			},
			{
				type: "text",
				left: "center",
				top: "50%",
				style: {
					text: formatNumberMaxDecimals(total),
					fill: COLORS.WHITE,
					fontSize: 16,
					fontWeight: "bold"
				}
			}
		]
	};
}
/**
 * Generates a configuration object for an ECharts pie chart that displays
 * the total emissions as text in the center of the chart.
 *
 * @param total - The total emission value to be displayed in the chart.
 * @returns An object containing the `graphic` property, which defines
 *          the text elements to be displayed in the center of the pie chart.
 */
export function getCloudAssetsTotalEmissionOverridesForPieChart(total: number) {
	return {
		series: {
			radius: ["30%", "80%"]
		},
		graphic: [
			{
				type: "text",
				left: "center",
				top: "45%",
				style: {
					text: "Total emission",
					fill: "#FFFFFF",
					fontSize: 14,
					fontWeight: "normal"
				}
			},
			{
				type: "text",
				left: "center",
				top: "50%",
				style: {
					text: formatNumberMaxDecimals(total),
					fill: COLORS.WHITE,
					fontSize: 16,
					fontWeight: "bold"
				}
			}
		]
	};
}
